import {
  flattenAdvancedFilters,
  resetAllGridFilters,
} from '@data-access/advanced-filters';
import {
  GridColumns,
  GridViewColumns,
  WcgAdvancedFilter,
  WcgMenuOption,
} from '@wcg/frontend-framework';
import { GridHandler, GridSortBy } from '../types';
import { WcgFilterIndicatorsOptions } from '@shared/interfaces/wcg-table-utils.interface';

export function updateGridFilter(
  filter: WcgFilterIndicatorsOptions,
  advancedFilters: WcgAdvancedFilter,
) {
  const _advancedFilters = advancedFilters;
  for (const [pKey, fValue] of Object.entries(filter)) {
    const resolvedKey = _advancedFilters[pKey as keyof WcgAdvancedFilter]
      ? pKey
      : (Object.keys(_advancedFilters).find(
          (k) => _advancedFilters[k]?.columnKey === pKey,
        ) as keyof WcgAdvancedFilter | undefined);

    if (!resolvedKey) {
      continue;
    }

    if (typeof fValue === 'object') {
      const _aValues = advancedFilters[resolvedKey].value as WcgMenuOption[];
      if (Array.isArray(_aValues) && _aValues.length > 0) {
        advancedFilters[resolvedKey].value = _aValues.filter((a) => {
          return (
            a.label.toLowerCase() !==
            `${fValue['label' as keyof typeof fValue]}`.toLowerCase()
          );
        });
      } else {
        advancedFilters[resolvedKey].value = [];
      }
    } else {
      if (fValue) {
        advancedFilters[resolvedKey]['value'] = [];
        // If filter is a date range, reset formatted and start/end values
        if (advancedFilters[resolvedKey]['formatted']) {
          advancedFilters[resolvedKey]['formatted'] = '';
          advancedFilters[resolvedKey]['start'] = '';
          advancedFilters[resolvedKey]['end'] = '';
        }
      }
    }
  }

  const filters: WcgAdvancedFilter[] = [];
  for (const [key, value] of Object.entries(_advancedFilters)) {
    filters.push({ [key]: { ...value, columnKey: value.columnKey ?? key } });
  }

  return {
    actions: 'filter',
    filters: filters,
  };
}

export function constructGridFilters(
  filters: WcgAdvancedFilter,
): WcgAdvancedFilter[] {
  const _constructFilter = updateGridFilter({}, filters);
  return Array.isArray(_constructFilter.filters) &&
    _constructFilter.filters.length > 0
    ? _constructFilter.filters
    : [];
}

export function handleGridUpdates(data: GridHandler) {
  if (data.searchString && !('searchValue' in data.params)) {
    data.params.searchValue = data.searchString;
  }
  if (data.sort && !('sortParam' in data.params)) {
    const sort = data.sort;
    data.params.sortParam = sort
      ? { field: sort.sortByField, dir: sort.sortByOrder }
      : null;
  }
  if (data.advancedFilters && !('filters' in data.params)) {
    data.params.filters = constructGridFilters(data.advancedFilters);
  }

  const _params = {
    ...data.params,
    page: data.params.page ?? data.pageView.page,
    pageSize: data.params.pageSize ?? data.pageView.pageSize,
  };

  if (data.actions.toLowerCase() === 'clear') {
    // Reset all filters to default state and update the grid with the reset filters
    const _constructFilter = resetAllGridFilters(data.advancedFilters);
    _params.filters = _constructFilter.filters;
    const filters = flattenAdvancedFilters(_params.filters!);
    if (filters) {
      data.callBacks.updateAdvancedFilters(filters);
    }
  }

  data.callBacks.loadGrid({
    params: data.wcgTableUtils.generateTableQueryParams(_params),
    updateColumns: data.updateColumns,
  });
  const filters = flattenAdvancedFilters(_params.filters!);
  const _updateAdvancedFilters = (filters: WcgAdvancedFilter | null) => {
    if (filters) {
      data.callBacks.updateAdvancedFilters(filters);
    }
  };
  switch (data.actions) {
    case 'filter':
      _updateAdvancedFilters(filters);
      break;

    case 'search':
      data.callBacks.updateSearchString(data.params.searchValue ?? '');
      break;

    case 'sort':
      data.callBacks.updateSort({
        dir: _params.sortParam?.dir as 'asc' | 'desc',
        field: _params.sortParam?.field ?? '',
      });
      break;

    case 'pagination':
      data.callBacks.updatePageView({
        page: _params.page,
        pageSize: _params.pageSize,
      });
      break;
    default:
      console.warn(`Unhandled action: ${data.actions}`);
      break;
  }

  data.callBacks.updateTableChange(data.isTableChanged);
}

export function getSortedColumn(columns: GridViewColumns): {
  sortByField: string;
  sortByOrder: 'asc' | 'desc' | '';
} {
  const sortedEntry = Object.entries(columns).find(
    ([, column]) => column.sorted === 'asc' || column.sorted === 'desc',
  );
  return {
    sortByField: sortedEntry?.[1].key ?? '',
    sortByOrder: sortedEntry?.[1].sorted ?? '',
  };
}

export const updateSortedColumn = (
  columns: GridColumns,
  sort: GridSortBy,
): GridColumns => {
  if (!sort) return columns;

  return Object.entries(columns).reduce((acc, [key, value]) => {
    acc[key] = {
      ...value,
      sorted: key === sort.sortByField ? sort.sortByOrder : null,
    };
    return acc;
  }, {} as GridColumns);
};
